import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Calendar, Trophy, Newspaper, MapPin, Image, ListOrdered, Ticket, Inbox, Users, Mail, FileText, Layers } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EventsAdmin from "@/pages/admin/EventsAdmin";
import ResultsAdmin from "@/pages/admin/ResultsAdmin";
import FormulaAdmin from "@/pages/admin/FormulaAdmin";
import ContentAdmin from "@/pages/admin/ContentAdmin";
import BlindStructuresAdmin from "@/pages/admin/BlindStructuresAdmin";
import { WinnersAdmin, NewsAdmin, VenuesAdmin, GalleryAdmin, ChampionStoriesAdmin, RegistrationsView, SubmissionsView, PlayersView, EmailTemplatesAdmin } from "@/pages/admin/MiscAdmin";
import { findDuplicatePairs, namesFromResults } from "@/lib/dedupe";

export default function AdminDashboard() {
  const [tab, setTab] = useState("events");
  const [data, setData] = useState({ events: [], results: [], news: [], winners: [], venues: [], gallery: [], registrations: [], submissions: [], players: [], blind_structures: [], seasons: [] });
  const [checkinStats, setCheckinStats] = useState({});
  const [formula, setFormula] = useState({ places: {}, default_points: 10 });
  const [payout, setPayout] = useState({ places: {}, default_points: 0 });
  const [currentSeason, setCurrentSeason] = useState("");

  const endpoints = {
    events: "/admin/all-events", results: "/results", news: "/admin/all-news", winners: "/winners", venues: "/venues",
    gallery: "/gallery", registrations: "/admin/registrations", submissions: "/admin/submissions", players: "/admin/players",
    blind_structures: "/blind-structures", seasons: "/season-list",
  };

  const loadAll = useCallback(() => {
    Object.entries(endpoints).forEach(([k, url]) => {
      api.get(url).then((r) => setData((d) => ({ ...d, [k]: r.data }))).catch(() => {});
    });
  }, []);

  useEffect(() => { loadAll(); api.get("/admin/checkin-stats").then((r) => setCheckinStats(r.data)).catch(() => {}); api.get("/points-formula").then((r) => setFormula(r.data)).catch(() => {}); api.get("/payout-formula").then((r) => setPayout(r.data)).catch(() => {}); api.get("/current-season").then((r) => setCurrentSeason(r.data.season || "")).catch(() => {}); }, [loadAll]);

  const reload = (key) => api.get(endpoints[key]).then((r) => setData((d) => ({ ...d, [key]: r.data }))).catch(() => {});

  const dupCount = useMemo(() => findDuplicatePairs(namesFromResults(data.results)).length, [data.results]);
  const playerNames = useMemo(() => [...new Set([...namesFromResults(data.results).map((n) => n.name), ...data.players.map((p) => p.name)].filter(Boolean))].sort(), [data.results, data.players]);

  const create = async (resource, body, key) => {
    try { await api.post(`/admin/${resource}`, body); toast.success("Added successfully"); reload(key); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); return false; }
  };

  const remove = async (resource, id, key) => {
    try { await api.delete(`/admin/${resource}/${id}`); toast.success("Deleted"); reload(key); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const update = async (resource, id, body, key) => {
    try { await api.put(`/admin/${resource}/${id}`, body); toast.success("Updated successfully"); reload(key); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); return false; }
  };

  const bulkResults = async (body) => {
    try { const { data } = await api.post("/admin/results/bulk", body); toast.success(`Added ${data.inserted} result${data.inserted === 1 ? "" : "s"}`); reload("results"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Bulk import failed"); return false; }
  };

  const rolloverSeason = async (newSeason) => {
    try { const { data } = await api.post("/admin/season-rollover", { new_season: newSeason }); setCurrentSeason(data.season); toast.success(`New season started: ${data.season}`); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start new season"); return false; }
  };

  void rolloverSeason;

  const mergePlayers = async (from, to) => {
    try { const { data } = await api.post("/admin/players/merge", { from_name: from, to_name: to }); if (data.merged === 0) { toast.info(`No results found for "${data.from}" — nothing to merge`); } else { toast.success(`Merged ${data.merged} result${data.merged === 1 ? "" : "s"} into ${data.to}`); } reload("results"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); return false; }
  };

  const bulkUpdateResults = async (rows) => {
    try { const { data } = await api.post("/admin/results/bulk-update", { rows }); toast.success(`Updated ${data.updated} result${data.updated === 1 ? "" : "s"}`); reload("results"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); return false; }
  };

  const undoMerge = async (logId) => {
    try { const { data } = await api.post(`/admin/merge-log/${logId}/undo`); toast.success(`Undone — restored ${data.restored} result${data.restored === 1 ? "" : "s"}`); reload("results"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); return false; }
  };

  const sendTestReminder = async () => {
    try { const { data } = await api.post("/admin/test-reminder"); toast.success(`Test reminder emailed to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send test email"); }
  };

  const approveSubmission = async (id) => {
    try { const { data } = await api.post(`/admin/submissions/${id}/approve`); toast.success(`Legend added · Member #${data.member_id}${data.email_sent ? "" : " (email could not be sent)"}`); reload("submissions"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); return false; }
  };

  const declineSubmission = async (id, reason) => {
    try { const { data } = await api.post(`/admin/submissions/${id}/decline`, { reason }); toast.success(`Applicant declined${data.email_sent ? " & emailed" : ""}`); reload("submissions"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); return false; }
  };

  const replicateEvent = async (id, body) => {
    try { await api.post(`/admin/events/${id}/replicate`, body); toast.success("Event replicated"); reload("events"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not replicate"); return false; }
  };

  const toggleEventVisibility = async (id, visible) => {
    try { await api.post(`/admin/events/${id}/visibility`, { visible }); toast.success(visible ? "Event now shown live" : "Event hidden from live page"); reload("events"); }
    catch (e) { toast.error("Could not update visibility"); }
  };

  const createSeason = async (body) => {
    try { await api.post("/admin/seasons", body); toast.success("Season created"); reload("seasons"); return true; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create season"); return false; }
  };
  const archiveSeason = async (id) => {
    try { await api.post(`/admin/seasons/${id}/archive`); reload("seasons"); }
    catch (e) { toast.error("Could not archive season"); }
  };
  const setDefaultSeason = async (id) => {
    try { const { data } = await api.post(`/admin/seasons/${id}/default`); setCurrentSeason(data.season); toast.success(`Default season: ${data.season}`); reload("seasons"); }
    catch (e) { toast.error("Could not set default season"); }
  };
  const deleteSeason = async (id) => {
    try { await api.delete(`/admin/seasons/${id}`); toast.success("Season removed"); reload("seasons"); }
    catch (e) { toast.error("Could not remove season"); }
  };

  const tabs = [
    { key: "events", label: "Events", icon: Calendar },
    { key: "results", label: "Results / Points", icon: ListOrdered },
    { key: "structures", label: "Blind Structures", icon: Layers },
    { key: "formula", label: "Points Formula", icon: ListOrdered },
    { key: "payout", label: "Payout Formula", icon: ListOrdered },
    { key: "winners", label: "Winners", icon: Trophy },
    { key: "news", label: "News", icon: Newspaper },
    { key: "venues", label: "Venues", icon: MapPin },
    { key: "gallery", label: "Gallery", icon: Image },
    { key: "registrations", label: "Registrations", icon: Ticket },
    { key: "submissions", label: "Enquiries", icon: Inbox },
    { key: "emails", label: "Emails", icon: Mail },
    { key: "players", label: "Players", icon: Users },
    { key: "content", label: "Page Content", icon: FileText },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-14" data-testid="admin-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-gold text-xs uppercase tracking-[0.35em] mb-2">Admin Control</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white">League Dashboard</h1>
          <p className="text-slate-400 mt-2 text-sm">Manage events, record placements &amp; points, update leaderboards, and review sign-ups.</p>
        </div>
        <Button onClick={sendTestReminder} variant="outline" className="rounded-none border-gold/40 text-gold hover:bg-gold hover:text-navy text-xs uppercase tracking-widest self-start" data-testid="test-reminder-btn">
          <Mail className="w-4 h-4 mr-2" /> Send test reminder to me
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto rounded-none bg-navy-paper border border-slate-800 p-1 gap-1 justify-start">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="rounded-none data-[state=active]:bg-gold data-[state=active]:text-navy text-xs">
              <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
              {t.key === "results" && dupCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-white text-[10px] font-bold" title={`${dupCount} possible duplicate player name${dupCount === 1 ? "" : "s"}`} data-testid="dup-badge">{dupCount}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="events"><EventsAdmin items={data.events} stats={checkinStats} currentSeason={currentSeason} venues={data.venues} seasons={data.seasons} blindStructures={data.blind_structures} onCreate={(b) => create("events", b, "events")} onUpdate={(id, b) => update("events", id, b, "events")} onDelete={(id) => remove("events", id, "events")} onReplicate={replicateEvent} onToggleVisibility={toggleEventVisibility} /></TabsContent>
        <TabsContent value="results"><ResultsAdmin items={data.results} events={data.events} formula={formula} currentSeason={currentSeason} seasons={data.seasons} onCreate={(b) => create("results", b, "results")} onUpdate={(id, b) => update("results", id, b, "results")} onDelete={(id) => remove("results", id, "results")} onBulk={bulkResults} onCreateSeason={createSeason} onArchiveSeason={archiveSeason} onSetDefaultSeason={setDefaultSeason} onDeleteSeason={deleteSeason} onMerge={mergePlayers} onBulkUpdate={bulkUpdateResults} onUndoMerge={undoMerge} /></TabsContent>
        <TabsContent value="structures"><BlindStructuresAdmin items={data.blind_structures} onCreate={(b) => create("blind-structures", b, "blind_structures")} onUpdate={(id, b) => update("blind-structures", id, b, "blind_structures")} onDelete={(id) => remove("blind-structures", id, "blind_structures")} /></TabsContent>
        <TabsContent value="formula"><FormulaAdmin formula={formula} onSave={setFormula} /></TabsContent>
        <TabsContent value="payout"><FormulaAdmin formula={payout} onSave={setPayout} endpoint="/admin/payout-formula" title="Prize Payout Split" desc="Set the percentage of the prize pool paid to each finishing position." unit="%" testPrefix="payout" /></TabsContent>
        <TabsContent value="winners"><WinnersAdmin items={data.winners} onCreate={(b) => create("winners", b, "winners")} onUpdate={(id, b) => update("winners", id, b, "winners")} onDelete={(id) => remove("winners", id, "winners")} /><ChampionStoriesAdmin /></TabsContent>
        <TabsContent value="news"><NewsAdmin items={data.news} onCreate={(b) => create("news", b, "news")} onUpdate={(id, b) => update("news", id, b, "news")} onDelete={(id) => remove("news", id, "news")} /></TabsContent>
        <TabsContent value="venues"><VenuesAdmin items={data.venues} onCreate={(b) => create("venues", b, "venues")} onUpdate={(id, b) => update("venues", id, b, "venues")} onDelete={(id) => remove("venues", id, "venues")} /></TabsContent>
        <TabsContent value="gallery"><GalleryAdmin items={data.gallery} events={data.events} seasons={data.seasons} players={playerNames} onCreate={(b) => create("gallery", b, "gallery")} onUpdate={(id, b) => update("gallery", id, b, "gallery")} onDelete={(id) => remove("gallery", id, "gallery")} /></TabsContent>
        <TabsContent value="registrations"><RegistrationsView items={data.registrations} /></TabsContent>
        <TabsContent value="submissions"><SubmissionsView items={data.submissions} onApprove={approveSubmission} onDecline={declineSubmission} /></TabsContent>
        <TabsContent value="emails"><EmailTemplatesAdmin /></TabsContent>
        <TabsContent value="players"><PlayersView items={data.players} onUpdate={(id, b) => update("players", id, b, "players")} /></TabsContent>
        <TabsContent value="content"><ContentAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
